import { useCallback, useEffect, useRef, useState } from "react";

import { ActionEventCenter } from "@/action-event-center";
import { AdminConsole } from "@/admin-console";
import { AssuranceCenter } from "@/assurance-center";
import { AppShell } from "@/components/app-shell";
import { ErrorState, Loading } from "@/components/empty-state";
import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { ContentEditor } from "@/content-editor";
import { loadWorkspace } from "@/lib/api";
import { navigation } from "@/lib/app-navigation";
import { permissionMap, type Permission, type View } from "@/lib/app-view";
import { configurationReady, supabase } from "@/lib/supabase";
import { surfaceHome, type AppSurface } from "@/lib/app-surface";
import { normalizeTenantIdentity } from "@/lib/tenant-domain";
import type { AppRole, Organisation } from "@/lib/types";
import { MissionControl } from "@/mission-control";
import { ObligationCenter } from "@/obligation-center";
import { ProcurementAudit } from "@/procurement-audit";
import { ReportingCenter } from "@/reporting-center";
import { RequestInbox } from "@/request-inbox";
import { TrustedNetworkAdmin } from "@/supplier-network";
import { TransactionWorkspace } from "@/transaction-workspace";
import { Approvals } from "@/screens/approvals";
import { Assets } from "@/screens/assets";
import { AuthScreen } from "@/screens/auth-screen";
import { Branding } from "@/screens/branding";
import { Budgets } from "@/screens/budgets";
import { Campaigns } from "@/screens/campaigns";
import { ConfigurationScreen } from "@/screens/configuration-screen";
import { Contracts } from "@/screens/contracts";
import { ControlPassports } from "@/screens/control-passports";
import { ControlledPilots } from "@/screens/controlled-pilots";
import { Dashboard } from "@/screens/dashboard";
import { Inventory } from "@/screens/inventory";
import { Invoices } from "@/screens/invoices";
import { IndustryTemplates } from "@/screens/industry-templates";
import { InternalControls } from "@/screens/internal-controls";
import { NetworkStructure } from "@/screens/network-structure";
import { Orders } from "@/screens/orders";
import { PlanEntitlements } from "@/screens/plan-entitlements";
import { ExportCenter } from "@/screens/export-center";
import { AccountingBridge } from "@/screens/accounting-bridge";
import { OrganisationOnboarding } from "@/screens/organisation-onboarding";
import { SetupGuide } from "@/screens/setup-guide";
import { Receipts } from "@/screens/receipts";
import { Reorders } from "@/screens/reorders";
import { Requisitions } from "@/screens/requisitions";
import { Rfqs } from "@/screens/rfqs";
import { SupplierPortal } from "@/screens/supplier-portal";
import { Team } from "@/screens/team";
import { Vendors } from "@/screens/vendors";

type Workspace = Awaited<ReturnType<typeof loadWorkspace>>;

const AUTH_SESSION_ERROR =
  /authentication required|auth session missing|jwt|token.*expired|invalid.*token|refresh token/i;

function isAuthSessionError(reason: unknown) {
  const message = reason instanceof Error ? reason.message : String(reason);
  return AUTH_SESSION_ERROR.test(message);
}

export default function App({
  tenantHint = null,
  onRequireAuthentication,
  surface = "workspace",
}: {
  tenantHint?: string | null;
  onRequireAuthentication?: () => void;
  surface?: AppSurface;
}) {
  const [workspace, setWorkspace] = useState<Workspace>();
  const [bootError, setBootError] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [organisationId, setOrganisationId] = useState(
    () => localStorage.getItem("raqoz_organisation") || "",
  );
  const [view, setView] = useState<View>(() => {
    if (typeof window === "undefined") return "dashboard";
    const requested = new URLSearchParams(window.location.search).get(
      "view",
    ) as View | null;
    return requested && navigation.some((item) => item.view === requested)
      ? requested
      : "dashboard";
  });
  const [query, setQuery] = useState(() =>
    typeof window === "undefined"
      ? ""
      : new URLSearchParams(window.location.search).get("record") || "",
  );
  const [recordId, setRecordId] = useState(() =>
    typeof window === "undefined"
      ? ""
      : new URLSearchParams(window.location.search).get("record") || "",
  );
  const [refresh, setRefresh] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const bootRequest = useRef(0);
  const updateOrganisation = useCallback((updated: Organisation) =>
    setWorkspace((current) =>
      current
        ? {
            ...current,
            organisations: current.organisations.map((item) =>
              item.id === updated.id ? updated : item,
            ),
          }
        : current,
    ), []);
  const boot = useCallback(async () => {
    const requestId = ++bootRequest.current;
    const isCurrent = () => requestId === bootRequest.current;
    setBootError("");
    setSignedOut(false);
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (!isCurrent()) return;
      if (sessionError || !sessionData.session) {
        setWorkspace(undefined);
        setSignedOut(true);
        setAuthReady(true);
        return;
      }

      let loaded: Workspace;
      try {
        loaded = await loadWorkspace();
      } catch (reason) {
        if (!isCurrent()) return;
        if (!isAuthSessionError(reason)) throw reason;

        const { data: refreshed, error: refreshError } =
          await supabase.auth.refreshSession();
        if (!isCurrent()) return;
        if (refreshError || !refreshed.session) {
          setWorkspace(undefined);
          setSignedOut(true);
          setAuthReady(true);
          return;
        }
        loaded = await loadWorkspace();
      }

      if (!isCurrent()) return;
      setWorkspace(loaded);
      setSignedOut(false);
      setAuthReady(true);
      setOrganisationId((current) => {
        const tenantOrganisation = tenantHint
          ? loaded.organisations.find(
              (organisation) =>
                normalizeTenantIdentity(organisation.slug) ===
                normalizeTenantIdentity(tenantHint),
            )
          : undefined;
        return (
          tenantOrganisation?.id ||
          (loaded.organisations.some(
            (organisation) => organisation.id === current,
          )
            ? current
            : loaded.organisations[0]?.id || "")
        );
      });
    } catch (reason) {
      if (!isCurrent()) return;
      setAuthReady(true);
      if (isAuthSessionError(reason)) {
        setWorkspace(undefined);
        setSignedOut(true);
        return;
      }
      setBootError(
        reason instanceof Error ? reason.message : "Unable to load RAQOZ",
      );
    }
  }, [tenantHint]);
  useEffect(() => {
    let live = true;
    void boot();
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!live) return;
      if (!session) {
        bootRequest.current += 1;
        setWorkspace(undefined);
        setSignedOut(true);
        setAuthReady(true);
        return;
      }
      if (
        event === "INITIAL_SESSION" ||
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED"
      ) {
        window.setTimeout(() => {
          if (live) void boot();
        }, 0);
      }
    });
    return () => {
      live = false;
      data.subscription.unsubscribe();
    };
  }, [boot]);
  useEffect(() => {
    if (organisationId)
      localStorage.setItem("raqoz_organisation", organisationId);
  }, [organisationId]);
  useEffect(() => {
    if (signedOut && onRequireAuthentication) onRequireAuthentication();
  }, [signedOut, onRequireAuthentication]);

  if (!configurationReady) return <ConfigurationScreen />;
  if (!authReady) return <Loading />;
  if (!workspace)
    return bootError ? (
      <ErrorState message={bootError} />
    ) : signedOut && onRequireAuthentication ? (
      <Loading />
    ) : (
      <AuthScreen
        onAuthenticated={boot}
        key={signedOut ? "signed-out" : "auth"}
      />
    );
  if (surface === "mission") {
    return workspace.platformOwner ? (
      <MissionControl user={workspace.user} />
    ) : (
      <SurfaceAccessBoundary
        title="Mission Control is restricted"
        description="Only active Cyclotron platform administrators can enter this operational surface."
        target="workspace"
      />
    );
  }

  if (surface === "admin") {
    if (workspace.platformOwner)
      return (
        <SurfaceAccessBoundary
          title="Use Mission Control for platform operations"
          description="Platform administrators cannot use the customer Admin Console to enter tenant workspaces."
          target="mission"
        />
      );
    const administrationIds = new Set(
      workspace.memberships
        .filter((membership) => membership.role === "tenant_admin" && membership.status === "active")
        .map((membership) => membership.organisation_id),
    );
    const administrationOrganisations = workspace.organisations.filter((item) =>
      administrationIds.has(item.id),
    );
    if (!administrationOrganisations.length)
      return (
        <SurfaceAccessBoundary
          title="Organisation administrator access required"
          description="Your account has no active organisation-administrator membership."
          target="workspace"
        />
      );
    const administrationOrganisation =
      administrationOrganisations.find((item) => item.id === organisationId) ||
      administrationOrganisations[0];
    return (
      <AdminConsole
        workspace={workspace}
        organisation={administrationOrganisation}
        organisations={administrationOrganisations}
        setOrganisationId={setOrganisationId}
        organisationChanged={updateOrganisation}
      />
    );
  }

  if (workspace.platformOwner)
    return (
      <SurfaceAccessBoundary
        title="Platform account separated from tenant work"
        description="Open Mission Control for aggregate operations. Platform accounts do not enter customer procurement workspaces."
        target="mission"
      />
    );

  if (
    !workspace.platformOwner &&
    !workspace.memberships.length &&
    workspace.supplierAccess.length
  )
    return (
      <SupplierPortal
        access={workspace.supplierAccess[0]}
        userName={workspace.user.name}
        onSignOut={() => supabase.auth.signOut()}
      />
    );
  if (!workspace.organisations.length)
    return (
      <OrganisationOnboarding
        userName={workspace.user.name}
        onDone={() => {
          setView("setup");
          void boot();
        }}
        onSignOut={() => supabase.auth.signOut()}
      />
    );

  const organisation =
    workspace.organisations.find((item) => item.id === organisationId) ||
    workspace.organisations[0];
  const role: AppRole = workspace.platformOwner
    ? "platform_owner"
    : workspace.memberships.find(
        (item) => item.organisation_id === organisation.id,
      )?.role || "auditor";
  const can = (permission: Permission) =>
    permissionMap[role].includes("*") ||
    permissionMap[role].includes(permission);
  const canRequest =
    workspace.platformOwner ||
    workspace.memberships.some(
      (item) =>
        item.organisation_id === organisation.id && item.status === "active",
    );
  const visibleNavigation = navigation.filter(
    (item) =>
      role === "platform_owner" || !item.roles || item.roles.includes(role),
  );
  const effectiveView = visibleNavigation.some((item) => item.view === view)
    ? view
    : "dashboard";
  const changed = () => setRefresh((value) => value + 1);
  const organisationChanged = updateOrganisation;
  const navigateTo = (nextView: string, recordId?: string) => {
    const safeView = visibleNavigation.some((item) => item.view === nextView)
      ? (nextView as View)
      : "dashboard";
    setView(safeView);
    setQuery(recordId || "");
    setRecordId(recordId || "");
    setMobileOpen(false);
    const params = new URLSearchParams();
    if (safeView !== "dashboard") params.set("view", safeView);
    if (recordId) params.set("record", recordId);
    window.history.replaceState(
      {},
      "",
      `/app${params.size ? `?${params.toString()}` : ""}`,
    );
  };
  const actionPermissions = {
    requisition: can("requisition"),
    approve: can("approve"),
    procure: can("procure"),
    receive: can("receive"),
    finance: can("finance"),
    manage: can("manage"),
  };


  return (
    <AppShell
      workspace={workspace}
      organisation={organisation}
      role={role}
      visibleNavigation={visibleNavigation}
      effectiveView={effectiveView}
      mobileOpen={mobileOpen}
      setMobileOpen={setMobileOpen}
      setOrganisationId={setOrganisationId}
      navigateTo={navigateTo}
      refresh={refresh}
      actionPermissions={actionPermissions}
      query={query}
      setQuery={setQuery}
      setRecordId={setRecordId}
      recordId={recordId}
    >
          {effectiveView === "dashboard" && (
            <Dashboard
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              canRequest={canRequest}
              currentUserId={workspace.user.id}
              role={role}
              permissions={actionPermissions}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "setup" && (
            <SetupGuide
              organisation={organisation}
              refresh={refresh}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "actions" && (
            <ActionEventCenter
              organisationId={organisation.id}
              currentUserId={workspace.user.id}
              refresh={refresh}
              permissions={actionPermissions}
              onNavigate={navigateTo}
            />
          )}
          {effectiveView === "inbox" && (
            <RequestInbox
              organisationId={organisation.id}
              refresh={refresh}
              query={query}
              canRequisition={can("requisition")}
              canProcure={can("procure")}
              canManage={can("manage")}
              changed={changed}
            />
          )}
          {effectiveView === "requisitions" && (
            <Requisitions
              organisation={organisation}
              refresh={refresh}
              query={query}
              recordId={recordId}
              can={can}
              canRequest={canRequest}
              currentUserId={workspace.user.id}
              changed={changed}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "approvals" && (
            <Approvals
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              currentUserId={workspace.user.id}
              changed={changed}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "rfqs" && (
            <Rfqs
              organisation={organisation}
              refresh={refresh}
              query={query}
              recordId={recordId}
              can={can}
              changed={changed}
              role={role}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "orders" && (
            <Orders
              organisation={organisation}
              refresh={refresh}
              query={query}
              recordId={recordId}
              can={can}
              changed={changed}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "receipts" && (
            <Receipts
              organisation={organisation}
              refresh={refresh}
              query={query}
              recordId={recordId}
              can={can}
              role={role}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "invoices" && (
            <Invoices
              organisation={organisation}
              refresh={refresh}
              query={query}
              recordId={recordId}
              can={can}
              changed={changed}
              navigate={navigateTo}
            />
          )}
          {effectiveView === "money" && (
            <TransactionWorkspace
              organisationId={organisation.id}
              refresh={refresh}
              query={query}
              canFinance={can("finance")}
              canApprove={can("approve")}
              viewerRole={role}
              onNavigate={navigateTo}
              changed={changed}
            />
          )}
          {effectiveView === "value" && (
            <ObligationCenter
              organisationId={organisation.id}
              refresh={refresh}
              query={query}
              canProcure={can("procure")}
              canReceive={can("receive")}
              canApprove={can("approve")}
              changed={changed}
            />
          )}
          {effectiveView === "reports" && (
            <ReportingCenter
              organisation={organisation}
              canFinance={can("finance")}
            />
          )}
          {effectiveView === "exports" && (
            <ExportCenter organisation={organisation} />
          )}
          {effectiveView === "accounting" && (
            <AccountingBridge organisation={organisation} />
          )}
          {effectiveView === "assurance" && (
            <AssuranceCenter
              organisationId={organisation.id}
              refresh={refresh}
              query={query}
              role={role}
              canManage={can("manage")}
              canFinance={can("finance")}
              canApprove={can("approve")}
              canProcure={can("procure")}
              changed={changed}
            />
          )}
          {effectiveView === "control-passports" && (
            <ControlPassports
              organisationId={organisation.id}
              refresh={refresh}
              query={query}
              canManage={can("manage")}
              canProcure={can("procure")}
              canApprove={can("approve")}
              changed={changed}
            />
          )}
          {effectiveView === "internal-controls" && (
            <InternalControls
              organisationId={organisation.id}
              refresh={refresh}
              query={query}
              canManage={can("manage")}
              canApprove={can("approve")}
              canProcure={can("procure")}
              changed={changed}
            />
          )}
          {effectiveView === "industry-templates" && (
            <IndustryTemplates
              organisationId={organisation.id}
              refresh={refresh}
              canManage={can("manage")}
              changed={changed}
            />
          )}
          {effectiveView === "controlled-pilots" && (
            <ControlledPilots
              organisationId={organisation.id}
              refresh={refresh}
              changed={changed}
            />
          )}
          {effectiveView === "vendors" && (
            <Vendors
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
            />
          )}
          {effectiveView === "contracts" && (
            <Contracts
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
            />
          )}
          {effectiveView === "reorders" && (
            <Reorders
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
            />
          )}
          {effectiveView === "budgets" && (
            <Budgets
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
            />
          )}
          {effectiveView === "network" && (
            <NetworkStructure
              organisation={organisation}
              refresh={refresh}
              organisations={workspace.organisations}
              platformOwner={workspace.platformOwner}
              changed={changed}
            />
          )}
          {effectiveView === "inventory" && (
            <Inventory
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
            />
          )}
          {effectiveView === "assets" && (
            <Assets
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
            />
          )}
          {effectiveView === "campaigns" && (
            <Campaigns
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
            />
          )}
          {effectiveView === "plan" && (
            <PlanEntitlements
              organisation={organisation}
              refresh={refresh}
              platformOwner={workspace.platformOwner}
              changed={() => {
                changed();
                void boot();
              }}
            />
          )}
          {effectiveView === "team" && (
            <Team
              organisation={organisation}
              refresh={refresh}
              query={query}
              can={can}
              changed={changed}
              currentUserId={workspace.user.id}
            />
          )}
          {effectiveView === "branding" && (
            <Branding
              key={organisation.id}
              organisation={organisation}
              can={can}
              onSaved={organisationChanged}
            />
          )}
          {effectiveView === "content" && (
            <ContentEditor platformOwner={workspace.platformOwner} />
          )}
          {effectiveView === "trusted-network" && <TrustedNetworkAdmin />}
          {effectiveView === "audit" && (
            <ProcurementAudit
              organisationId={organisation.id}
              refresh={refresh}
              query={query}
            />
          )}
    </AppShell>
  );
}


function SurfaceAccessBoundary({
  title,
  description,
  target,
}: {
  title: string;
  description: string;
  target: AppSurface;
}) {
  return (
    <main className="grid min-h-screen place-items-center bg-[#f5f7f6] p-6">
      <section className="w-full max-w-xl rounded-[2rem] border border-[#dce5e1] bg-white p-8 shadow-xl sm:p-10">
        <RaqozLogo />
        <ShieldCheck className="mt-10 size-9 text-[#0f766e]" aria-hidden="true" />
        <h1 className="mt-5 font-serif text-3xl">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button onClick={() => window.location.assign(surfaceHome(target))}>
            Open {target === "mission" ? "Mission Control" : "RAQOZ workspace"}
          </Button>
          <Button variant="outline" onClick={() => void supabase.auth.signOut()}>
            Sign out
          </Button>
        </div>
      </section>
    </main>
  );
}
